import { ArrowLeft, X } from 'lucide-react';
import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { Button } from '../../../../components/Button';
import styles from './PaneFrame.module.css';

/** Where People's pane is drawn: beside the list, over it (laptop and tablet), or as its own screen (phone). */
export type PaneMode = 'docked' | 'overlay' | 'page';

/** What Tab can reach inside the pane. */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * People's pane (FRAME-SPEC §4 L1 and §5): a fixed header, and a body that scrolls by itself. On a phone it is a
 * screen of its own, with "People" to go back instead of a close button. Esc closes it, after any pop-up opened
 * from inside it.
 *
 * Over the list (1180px and below, review R9) it behaves as a drawer does (components/Drawer.tsx): it takes the
 * focus when it opens, Tab stays inside it, and closing it gives the focus back to what opened it.
 */
export function PaneFrame({
  mode,
  label,
  title,
  sub,
  leading,
  nav,
  onClose,
  onKeyDown,
  children,
}: {
  readonly mode: PaneMode;
  readonly label: string;
  readonly title: string;
  readonly sub?: ReactNode;
  readonly leading?: ReactNode;
  /** Beside Close: "6 of 142" and the previous and next buttons. */
  readonly nav?: ReactNode;
  readonly onClose: () => void;
  readonly onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
  readonly children: ReactNode;
}): ReactNode {
  const paneRef = useRef<HTMLElement | null>(null);
  const isOverlay = mode === 'overlay';

  useEffect(() => {
    if (!isOverlay) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    paneRef.current?.focus();
    return () => {
      if (opener?.isConnected) opener.focus();
    };
  }, [isOverlay]);

  const keyDown = (event: KeyboardEvent<HTMLElement>): void => {
    onKeyDown?.(event);
    if (isOverlay && event.key === 'Tab' && !event.defaultPrevented) {
      keepFocusInside(event, paneRef.current);
      return;
    }
    if (event.key !== 'Escape' || event.defaultPrevented) return;
    // A pop-up opened from inside the pane closes first.
    if (document.querySelector('[data-popover]')) return;
    event.stopPropagation();
    onClose();
  };

  return (
    <aside ref={paneRef} className={styles.pane} data-mode={mode} aria-label={label} tabIndex={-1} onKeyDown={keyDown}>
      <header className={styles.head}>
        {mode === 'page' && (
          <Button variant="ghost" size="sm" icon={ArrowLeft} onClick={onClose}>
            People
          </Button>
        )}
        <div className={styles.who}>
          {leading}
          <span className={styles.whoText}>
            <h2 className={styles.title}>{title}</h2>
            {sub !== undefined && <span className={styles.sub}>{sub}</span>}
          </span>
        </div>
        <div className={styles.nav}>
          {nav}
          {mode !== 'page' && <Button variant="ghost" size="sm" icon={X} aria-label="Close" onClick={onClose} />}
        </div>
      </header>
      <div className={styles.body}>{children}</div>
    </aside>
  );
}

/** One block of the pane, divided from the next by a line, its title bold ink (CLAUDE.md §5). */
export function PaneSection({
  title,
  label,
  children,
}: {
  readonly title?: ReactNode;
  /** The section's name for a screen reader, where it has no visible title. */
  readonly label?: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <section className={styles.section} aria-label={label ?? (typeof title === 'string' ? title : undefined)}>
      {title !== undefined && <h3 className={styles.sectionTitle}>{title}</h3>}
      {children}
    </section>
  );
}

/** A card-shaped editor (roles, free times) shown as one more section of the pane: no outline, band or shadow. */
export function PaneEmbed({ children }: { readonly children: ReactNode }): ReactNode {
  return <div className={styles.embed}>{children}</div>;
}

/** Tab from the last control goes to the first, Shift+Tab from the first (or the pane itself) to the last. */
function keepFocusInside(event: KeyboardEvent<HTMLElement>, pane: HTMLElement | null): void {
  if (!pane) return;
  // A pop-up opened from the pane is portalled outside it and keeps its own Tab order.
  if (event.target instanceof Node && !pane.contains(event.target)) return;
  const focusable = [...pane.querySelectorAll<HTMLElement>(FOCUSABLE)];
  const first = focusable[0];
  const last = focusable.at(-1);
  if (!first || !last) {
    event.preventDefault();
    return;
  }
  const active = document.activeElement;
  if (event.shiftKey && (active === first || active === pane)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}
